import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authApi, uploadApi, geocodeApi, paymentApi } from '../lib/api'
import Avatar from '../components/Avatar'
import Modal from '../components/Modal'
import NotificationsTab from '../components/NotificationsTab'
import { US_STATES, findCity } from '../data/us-cities'
import { v4 as uuidv4 } from 'uuid'
import { addressProblems } from '../utils/locations'

interface LocationEntry {
  id: string
  name: string
  address: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  instructions?: string
  isDefault?: boolean
  latitude?: string
  longitude?: string
}

type LocationType = 'pickup' | 'drop-off'

interface UserMetadata {
  locations?: Partial<Record<LocationType, LocationEntry[]>>
}

interface ProfileData {
  firstName: string
  lastName: string
  email: string
  phone: string
  avatar: string
  authProvider: string
  metadata: UserMetadata
}

const locationTypes: { key: LocationType; label: string }[] = [
  { key: 'pickup', label: 'Pickup locations' },
  { key: 'drop-off', label: 'Shipping locations' },
]

function LocationsTab({
  metadata,
  setMetadata,
  onSaved,
  showSnackbar,
}: {
  metadata: UserMetadata
  setMetadata: (m: UserMetadata) => void
  onSaved?: () => void
  showSnackbar: (message: string) => void
}) {
  const MAX_LOCATIONS = 5
  const [isSaving, setIsSaving] = useState(false)

  const emptyLocation = (): LocationEntry => ({
    id: uuidv4(),
    name: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    instructions: '',
    isDefault: false,
  })

  const locationsFor = (key: LocationType) => metadata.locations?.[key] || []

  const updateList = (key: LocationType, next: LocationEntry[]) => {
    setMetadata({
      locations: {
        ...(metadata.locations || {}),
        [key]: next,
      } as Partial<Record<LocationType, LocationEntry[]>>,
    })
  }

  const persist = async (nextMetadata: UserMetadata): Promise<UserMetadata> => {
    setIsSaving(true)
    try {
      const cleaned: UserMetadata = { locations: {} }
      locationTypes.forEach(({ key }) => {
        const list = nextMetadata.locations?.[key]?.filter((entry) =>
          entry.name.trim() || entry.address.trim() || entry.city?.trim() || entry.state?.trim() || entry.zip?.trim()
        ) || []
        if (list.length > 0) {
          cleaned.locations![key] = list
        }
      })
      const response = await authApi.updateProfile({ metadata: cleaned })
      const savedMetadata: UserMetadata = response.data?.data?.metadata || cleaned
      setMetadata(savedMetadata)
      onSaved?.()
      return savedMetadata
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save locations'
      throw new Error(message)
    } finally {
      setIsSaving(false)
    }
  }

  const setDefault = async (key: LocationType, index: number) => {
    const list = locationsFor(key).map((entry, i) => ({ ...entry, isDefault: i === index }))
    const next: UserMetadata = {
      locations: {
        ...(metadata.locations || {}),
        [key]: list,
      } as Partial<Record<LocationType, LocationEntry[]>>,
    }
    setMetadata(next)
    try {
      await persist(next)
    } catch (err: any) {
      showSnackbar(err.message)
    }
  }

  const [confirmRemove, setConfirmRemove] = useState<{ key: LocationType; index: number; entry: LocationEntry } | null>(null)

  const confirmRemoveEntry = (key: LocationType, index: number) => {
    const entry = locationsFor(key)[index]
    if (!entry) return
    setConfirmRemove({ key, index, entry })
  }

  const executeRemove = async () => {
    if (!confirmRemove) return
    const { key, index } = confirmRemove
    const remaining = locationsFor(key).filter((_, i) => i !== index)
    // There is always exactly one default: if the default was the one removed, the first
    // remaining address takes over.
    const list = remaining.length > 0 && !remaining.some((entry) => entry.isDefault)
      ? remaining.map((entry, i) => ({ ...entry, isDefault: i === 0 }))
      : remaining
    const next: UserMetadata = {
      locations: {
        ...(metadata.locations || {}),
        [key]: list,
      } as Partial<Record<LocationType, LocationEntry[]>>,
    }
    setConfirmRemove(null)
    setMetadata(next)
    try {
      await persist(next)
    } catch (err: any) {
      showSnackbar(err.message)
    }
  }

  const [modalType, setModalType] = useState<LocationType | null>(null)
  const [draft, setDraft] = useState<LocationEntry>(emptyLocation())
  const [clarifyCity, setClarifyCity] = useState<{ state: string; city: string } | null>(null)

  const openModal = (key: LocationType) => {
    setDraft({ ...emptyLocation(), isDefault: locationsFor(key).length === 0 })
    setModalType(key)
  }

  const closeModal = () => {
    setModalType(null)
    setDraft(emptyLocation())
    setClarifyCity(null)
  }

  const resolveCityCoordinates = (entry: LocationEntry): LocationEntry => {
    if (!entry.city || !entry.state) return entry
    const matched = findCity(entry.state, entry.city)
    if (matched) {
      return { ...entry, city: matched.name, latitude: matched.lat, longitude: matched.lng }
    }
    return { ...entry, latitude: undefined, longitude: undefined }
  }

  // Tries to geocode the actual street address (accurate to the real pickup spot, not just
  // the city center) before falling back to the city-centroid lookup. Never throws — a
  // failed/timed-out geocode just means the caller falls back to the existing behavior.
  const resolveAddressCoordinates = async (entry: LocationEntry): Promise<LocationEntry | null> => {
    if (!entry.address?.trim() || !entry.city?.trim() || !entry.state?.trim()) return null
    try {
      const response = await geocodeApi.resolve({
        address: entry.address,
        city: entry.city,
        state: entry.state,
        zip: entry.zip,
      })
      if (response.data.success) {
        return { ...entry, latitude: String(response.data.data.latitude), longitude: String(response.data.data.longitude) }
      }
      return null
    } catch {
      return null
    }
  }

  const handleSaveModal = async () => {
    if (!modalType) return
    const key = modalType

    // A location must be an actual physical address: street, city, state and ZIP (no PO boxes).
    const problems = addressProblems(draft)
    if (problems.length > 0) {
      showSnackbar(`Please enter ${problems.join(', ')} before saving this location.`)
      return
    }

    setIsSaving(true)
    const geocoded = await resolveAddressCoordinates(draft)
    const resolved = geocoded || resolveCityCoordinates(draft)
    if (!resolved.latitude || !resolved.longitude) {
      setIsSaving(false)
      setClarifyCity({ state: resolved.state || '', city: resolved.city || '' })
      return
    }

    const current = locationsFor(key)
    const list = [...current, resolved]
    const next: UserMetadata = {
      locations: {
        ...(metadata.locations || {}),
        [key]: list,
      } as Partial<Record<LocationType, LocationEntry[]>>,
    }
    updateList(key, list)
    closeModal()
    try {
      await persist(next)
    } catch (err: any) {
      showSnackbar(err.message)
    }
  }

  const handleConfirmClarify = async () => {
    if (!modalType || !clarifyCity) return
    const key = modalType
    const current = locationsFor(key)
    const list = [...current, { ...draft, city: clarifyCity.city, state: clarifyCity.state }]
    const next: UserMetadata = {
      locations: {
        ...(metadata.locations || {}),
        [key]: list,
      } as Partial<Record<LocationType, LocationEntry[]>>,
    }
    updateList(key, list)
    closeModal()
    try {
      await persist(next)
    } catch (err: any) {
      showSnackbar(err.message)
    }
  }

  const updateDraft = (field: keyof LocationEntry, value: string | boolean) => {
    setDraft(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <p style={{ margin: 0, color: 'var(--slate)', fontSize: '0.9rem' }}>
        Both owners and renters need a real street address (street, city, state and ZIP; no PO boxes). As an owner, add a pickup location before listing an item so renters know where to collect it. As a renter, add a shipping address before renting. You can save as many as you like; one is always your default.
      </p>

      {isSaving && (
        <p style={{ margin: 0, color: 'var(--forest-green)', fontSize: '0.875rem' }}>Saving locations...</p>
      )}

      {locationTypes.map(({ key, label }) => {
        const list = locationsFor(key)
        const atLimit = list.length >= MAX_LOCATIONS
        return (
          <div key={key} style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{label}</h3>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{list.length}/{MAX_LOCATIONS}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {list.length === 0 && (
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>No {label.toLowerCase()} saved yet.</p>
              )}
              {list.map((entry, index) => (
                <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-primary)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.name || 'Untitled location'}{entry.isDefault ? ' (default)' : ''}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.address}{entry.city ? `, ${entry.city}` : ''}{entry.state ? `, ${entry.state}` : ''}{entry.zip ? ` ${entry.zip}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    {!entry.isDefault && (
                      <button
                        type="button"
                        onClick={() => setDefault(key, index)}
                        disabled={isSaving}
                        style={{ fontSize: '0.75rem', color: 'var(--forest-green)', background: 'none', border: 'none', cursor: isSaving ? 'not-allowed' : 'pointer' }}
                      >
                        Set default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => confirmRemoveEntry(key, index)}
                      disabled={isSaving}
                      style={{ fontSize: '0.75rem', color: 'var(--error-text)', background: 'none', border: 'none', cursor: isSaving ? 'not-allowed' : 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => openModal(key)}
              disabled={atLimit || isSaving}
              style={{
                marginTop: '0.75rem',
                fontSize: '0.875rem',
                color: atLimit ? 'var(--text-muted)' : 'var(--forest-green)',
                background: 'none',
                border: 'none',
                cursor: atLimit || isSaving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
              }}
            >
              {atLimit ? `Max ${MAX_LOCATIONS} ${label.toLowerCase()} reached` : `+ Add ${label.split(' ')[0].toLowerCase()} location`}
            </button>
          </div>
        )
      })}

      <Modal
        isOpen={!!modalType && !clarifyCity}
        title={modalType ? `Add ${locationTypes.find(l => l.key === modalType)?.label.split(' ')[0]} location` : ''}
        confirmLabel="Add location"
        cancelLabel="Cancel"
        onConfirm={handleSaveModal}
        onCancel={closeModal}
        disabled={isSaving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <input type="text" placeholder="Name (e.g. Home)" value={draft.name} onChange={(e) => updateDraft('name', e.target.value)} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }} />
          <input type="text" placeholder="Street address (required)" value={draft.address} onChange={(e) => updateDraft('address', e.target.value)} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }} />
          <input type="text" placeholder="Address 2 (apt / suite — optional)" value={draft.address2 || ''} onChange={(e) => updateDraft('address2', e.target.value)} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <input type="text" placeholder="City" value={draft.city || ''} onChange={(e) => updateDraft('city', e.target.value)} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }} />
            <select value={draft.state || ''} onChange={(e) => updateDraft('state', e.target.value)} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem', backgroundColor: 'white' }}>
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
          <input type="text" placeholder="ZIP (required)" value={draft.zip || ''} onChange={(e) => updateDraft('zip', e.target.value)} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem' }} />
          <textarea placeholder="Notes / Instructions" value={draft.instructions || ''} onChange={(e) => updateDraft('instructions', e.target.value)} rows={2} disabled={isSaving} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '0.875rem', resize: 'vertical' }} />
        </div>
      </Modal>

      <Modal
        isOpen={!!clarifyCity}
        title="City not recognized"
        confirmLabel="Save anyway"
        cancelLabel="Go back"
        onConfirm={handleConfirmClarify}
        onCancel={() => setClarifyCity(null)}
        disabled={isSaving}
      >
        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem' }}>
          We couldn't find <strong>{clarifyCity?.city}</strong>, {clarifyCity?.state} in our database, so we can't set map coordinates automatically.
        </p>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          Please check the spelling or pick a nearby city. You can still save this location, but some delivery options may not work without coordinates.
        </p>
      </Modal>

      <Modal
        isOpen={!!confirmRemove}
        title="Remove location"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={executeRemove}
        onCancel={() => setConfirmRemove(null)}
        disabled={isSaving}
      >
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Are you sure you want to remove <strong>{confirmRemove?.entry.name || 'this location'}</strong>? This cannot be undone.
        </p>
      </Modal>
    </div>
  )
}

export default function Profile() {
  const { refreshUser, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNewUser = searchParams.get('newUser') === 'true'
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState<ProfileData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    avatar: '',
    authProvider: 'LOCAL',
    metadata: {},
  })
  const [draftMetadata, setDraftMetadata] = useState<UserMetadata>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [message, setMessage] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false)

  const tabParam = searchParams.get('tab')
  const initialTab =
    tabParam === 'payouts' ? 'payouts' : tabParam === 'notifications' ? 'notifications' : 'profile'
  const [activeTab, setActiveTab] = useState<'profile' | 'locations' | 'payouts' | 'notifications' | 'account'>(initialTab)

  interface ConnectStatus {
    connected: boolean
    payoutsEnabled: boolean
    detailsSubmitted: boolean
  }
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null)
  const [isLoadingConnectStatus, setIsLoadingConnectStatus] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletePassword, setDeletePassword] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await authApi.getProfile()
        const data = response.data.data
        setFormData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          avatar: data.avatar || '',
          authProvider: data.authProvider || 'LOCAL',
          metadata: data.metadata || {},
        })
        setDraftMetadata(data.metadata || {})
      } catch (err: any) {
        showSnackbar(err?.response?.data?.error || 'Failed to load profile')
      } finally {
        setIsLoading(false)
      }
    }

    loadProfile()
  }, [])

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message)
    setIsSnackbarVisible(true)
    window.setTimeout(() => {
      setIsSnackbarVisible(false)
    }, 5000)
  }

  useEffect(() => {
    if (activeTab !== 'payouts' || connectStatus || isLoadingConnectStatus) return
    setIsLoadingConnectStatus(true)
    paymentApi.getConnectStatus()
      .then((response) => setConnectStatus(response.data.data))
      .catch((err: any) => showSnackbar(err?.response?.data?.error || 'Failed to load payout status'))
      .finally(() => setIsLoadingConnectStatus(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const handleConnectPayouts = async () => {
    setIsConnecting(true)
    try {
      const response = await paymentApi.createConnectAccount()
      const url = response.data?.data?.url
      if (url) {
        window.location.href = url
      } else {
        showSnackbar('Failed to start Stripe onboarding')
        setIsConnecting(false)
      }
    } catch (err: any) {
      showSnackbar(err?.response?.data?.error || 'Failed to start Stripe onboarding')
      setIsConnecting(false)
    }
  }

  const isOAuth = formData.authProvider !== 'LOCAL'

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Type DELETE exactly to confirm')
      return
    }
    if (!isOAuth && !deletePassword) {
      setDeleteError('Enter your current password')
      return
    }

    setIsDeleting(true)
    setDeleteError('')

    try {
      const response = await authApi.deleteMe({ password: isOAuth ? '' : deletePassword })
      if (response.data.success) {
        logout()
      } else {
        setDeleteError(response.data.error || 'Failed to delete account')
      }
    } catch (err: any) {
      const data = err?.response?.data
      setDeleteError(data?.error || 'Failed to delete account')
    } finally {
      setIsDeleting(false)
    }
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setIsDeleteModalOpen(false)
    setDeleteConfirmText('')
    setDeletePassword('')
    setDeleteError('')
  }

  const tabButtonStyle = (tab: 'profile' | 'locations' | 'payouts' | 'notifications' | 'account') => ({
    // grow to fill on desktop, but never shrink below the label's own width -- with
    // five tabs, `flex: 1` alone let the row overflow the card on a phone and pushed
    // "Account" off the right edge entirely.
    flex: '1 0 auto',
    whiteSpace: 'nowrap' as const,
    padding: '0.75rem',
    fontWeight: 600,
    fontSize: '0.95rem',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--forest-green)' : '2px solid transparent',
    color: activeTab === tab ? 'var(--forest-green)' : 'var(--slate)',
    background: 'transparent',
    cursor: 'pointer',
  })

  const tabRowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const row = tabRowRef.current
    if (!row) return
    const active = row.querySelector<HTMLElement>('[data-active="true"]')
    // `block: 'nearest'` keeps this from cascading into a vertical page scroll --
    // the same trap the message-thread auto-scroll hit.
    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [activeTab])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
    let formatted = digits
    if (digits.length > 6) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    } else if (digits.length > 3) {
      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    }
    setFormData({ ...formData, phone: formatted })
    if (fieldErrors.phone) {
      setFieldErrors(prev => ({ ...prev, phone: '' }))
    }
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setMessage('')

    try {
      const response = await uploadApi.avatar(file)
      if (response.data.success) {
        setFormData({ ...formData, avatar: response.data.data.url })
        setMessage('Avatar uploaded')
      } else {
        showSnackbar(response.data.error || 'Failed to upload avatar')
      }
    } catch (err: any) {
      showSnackbar(err?.response?.data?.error || 'Failed to upload avatar')
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setMessage('')
    setFieldErrors({})

    try {
      const payload: Partial<ProfileData> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
      }
      if (formData.avatar) {
        payload.avatar = formData.avatar
      }

      const response = await authApi.updateProfile(payload)

      if (response.data.success) {
        setMessage('Profile saved successfully')
        showSnackbar('Profile saved successfully')
        const refreshed = await refreshUser()
        if (refreshed) {
          navigate('/dashboard')
        } else {
          showSnackbar('Profile saved, but session could not be refreshed. Please log in again.')
        }
      } else {
        showSnackbar(response.data.error || 'Failed to save profile')
      }
    } catch (err: any) {
      const data = err?.response?.data
      if (data?.details && Array.isArray(data.details) && data.details.length > 0) {
        const nextErrors: Record<string, string> = {}
        data.details.forEach((d: any) => {
          const field = d.field || d.param
          if (field) nextErrors[field] = d.message
        })
        setFieldErrors(nextErrors)
        const first = data.details[0]
        showSnackbar(first?.message || 'Validation failed')
      } else {
        showSnackbar(data?.error || 'Failed to save profile')
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <header style={{
        background: '#fff',
        borderBottom: '1px solid var(--border)',
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 1000,
        padding: '1rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 clamp(1rem, 4vw, 2rem)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
            <img src="/IGotThat_logo_bk.svg" alt="I Got That U.S." width={230} height={34} style={{ width: '230px', height: 'auto', display: 'block' }} />
          </Link>
          <Link to="/dashboard" style={{ color: 'var(--slate)', textDecoration: 'none', fontSize: '0.9rem' }}>
            ← Back to Dashboard
          </Link>
        </div>
      </header>

      <main style={{ paddingTop: 'var(--auth-header-height)', paddingBottom: '80px', background: 'var(--bg-primary)', minHeight: 'calc(100vh - var(--auth-header-height))' }}>
        <div style={{ width: '100%', maxWidth: '640px', padding: '1rem', margin: '0 auto' }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--border)',
            padding: 'clamp(1.5rem, 5vw, 2.5rem)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>
                {isNewUser ? 'Complete Your Profile' : 'Profile'}
              </h1>
              <p style={{ color: 'var(--slate)', margin: 0 }}>
                Manage your profile and account
              </p>
            </div>

            <div
              ref={tabRowRef}
              style={{
                display: 'flex',
                borderBottom: '1px solid var(--border)',
                marginBottom: '1.5rem',
                overflowX: 'auto',
                scrollbarWidth: 'none',
              }}
            >
              <button type="button" data-active={activeTab === 'profile'} style={tabButtonStyle('profile')} onClick={() => setActiveTab('profile')}>Profile</button>
              <button type="button" data-active={activeTab === 'locations'} style={tabButtonStyle('locations')} onClick={() => setActiveTab('locations')}>Locations</button>
              <button type="button" data-active={activeTab === 'payouts'} style={tabButtonStyle('payouts')} onClick={() => setActiveTab('payouts')}>Payouts</button>
              <button type="button" data-active={activeTab === 'notifications'} style={tabButtonStyle('notifications')} onClick={() => setActiveTab('notifications')}>Notifications</button>
              <button type="button" data-active={activeTab === 'account'} style={tabButtonStyle('account')} onClick={() => setActiveTab('account')}>Account</button>
            </div>

            {activeTab === 'profile' && message && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: '#C6F6D5', color: '#276749', fontSize: '0.875rem', border: '1px solid #9AE6B4' }}>
                {message}
              </div>
            )}

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--slate)' }}>Loading profile...</div>
            ) : activeTab === 'profile' ? (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Avatar */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={handleAvatarClick}
                    disabled={isUploading}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      borderRadius: '50%'
                    }}
                  >
                    <Avatar
                      firstName={formData.firstName}
                      lastName={formData.lastName}
                      avatar={formData.avatar}
                      size={96}
                    />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={handleAvatarClick}
                    disabled={isUploading}
                    style={{
                      fontSize: '0.875rem',
                      color: 'var(--forest-green)',
                      background: 'none',
                      border: 'none',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {isUploading ? 'Uploading...' : formData.avatar ? 'Change avatar' : 'Add avatar'}
                  </button>
                </div>

                {/* Name */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }} className="sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                      First Name <span style={{ color: 'var(--error-text)' }}>*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: `1px solid ${fieldErrors.firstName ? 'var(--error-text)' : 'var(--border)'}`,
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                      required
                    />
                    {fieldErrors.firstName && <div style={{ fontSize: '0.75rem', color: 'var(--error-text)', marginTop: '0.25rem' }}>{fieldErrors.firstName}</div>}
                  </div>
                  <div>
                    <label htmlFor="lastName" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                      Last Name <span style={{ color: 'var(--error-text)' }}>*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: `1px solid ${fieldErrors.lastName ? 'var(--error-text)' : 'var(--border)'}`,
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                      required
                    />
                    {fieldErrors.lastName && <div style={{ fontSize: '0.75rem', color: 'var(--error-text)', marginTop: '0.25rem' }}>{fieldErrors.lastName}</div>}
                  </div>
                </div>

                {/* Email (read-only) */}
                <div>
                  <label htmlFor="email" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    readOnly
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', background: '#F7FAF7', color: 'var(--slate)' }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Phone Number <span style={{ color: 'var(--error-text)' }}>*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `1px solid ${fieldErrors.phone ? 'var(--error-text)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    required
                  />
                  {fieldErrors.phone && <div style={{ fontSize: '0.75rem', color: 'var(--error-text)', marginTop: '0.25rem' }}>{fieldErrors.phone}</div>}
                  {!fieldErrors.phone && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--slate-light)', marginTop: '0.25rem', marginBottom: 0 }}>
                      US/Canada numbers only during beta.
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }} className="flex-col sm:flex-row">
                  <Link
                    to="/dashboard"
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      background: 'white',
                      color: 'var(--text-secondary)',
                      fontWeight: 600,
                      fontSize: '1rem',
                      textAlign: 'center',
                      textDecoration: 'none'
                    }}
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={isSaving || isUploading}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'var(--forest-green)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.5 : 1
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save Profile'}
                  </button>
                </div>
              </form>
            ) : activeTab === 'locations' ? (
              <LocationsTab
                metadata={draftMetadata}
                setMetadata={setDraftMetadata}
                onSaved={async () => {
                  await refreshUser()
                  showSnackbar('Locations saved')
                }}
                showSnackbar={showSnackbar}
              />
            ) : activeTab === 'notifications' ? (
              <NotificationsTab showSnackbar={showSnackbar} />
            ) : activeTab === 'payouts' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <p style={{ margin: 0, color: 'var(--slate)', fontSize: '0.9rem' }}>
                  Connect a Stripe account to get paid. As soon as a renter picks up (or a shipment is delivered),
                  your share of the rental is sent to this account automatically — no need to wait for the rental to finish.
                </p>

                {isLoadingConnectStatus ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--slate)' }}>Loading payout status...</div>
                ) : (
                  <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: connectStatus?.payoutsEnabled ? 'var(--bright-green)' : connectStatus?.connected ? '#F7A82F' : 'var(--text-muted)',
                      }} />
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                        {connectStatus?.payoutsEnabled
                          ? 'Payouts enabled'
                          : connectStatus?.connected
                            ? 'Onboarding incomplete'
                            : 'Not connected'}
                      </span>
                    </div>

                    <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: 'var(--slate)', lineHeight: 1.5 }}>
                      {connectStatus?.payoutsEnabled
                        ? "You're all set. Payouts will be sent to this account automatically."
                        : connectStatus?.connected
                          ? "You've started connecting a Stripe account, but Stripe still needs a few more details (identity, bank account) before payouts can go through."
                          : 'No Stripe account connected yet. Owners need this set up before a rental payout can be sent.'}
                    </p>

                    <button
                      type="button"
                      onClick={handleConnectPayouts}
                      disabled={isConnecting}
                      style={{
                        padding: '0.75rem 1rem',
                        background: 'var(--forest-green)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        borderRadius: '8px',
                        border: 'none',
                        cursor: isConnecting ? 'not-allowed' : 'pointer',
                        opacity: isConnecting ? 0.6 : 1,
                      }}
                    >
                      {isConnecting
                        ? 'Redirecting to Stripe...'
                        : connectStatus?.connected
                          ? 'Finish onboarding with Stripe'
                          : 'Connect with Stripe'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Account</h2>
                  <p style={{ color: 'var(--slate)', margin: 0, fontSize: '0.9rem' }}>
                    Email on file: <strong style={{ color: 'var(--text-primary)' }}>{formData.email}</strong>
                  </p>
                </div>

                <div style={{
                  border: '1px solid var(--error-text)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  background: 'rgba(229, 62, 62, 0.04)'
                }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--error-text)', margin: '0 0 0.5rem 0' }}>Danger zone</h3>
                  <p style={{ color: 'var(--slate)', fontSize: '0.875rem', margin: '0 0 1rem 0', lineHeight: 1.5 }}>
                    Deleting your account will remove your profile data, cart, and any listings with no rental history.
                    Completed rentals, reviews, and messages will remain under an anonymized name.
                    This cannot be undone.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsDeleteModalOpen(true)}
                    style={{
                      padding: '0.75rem 1rem',
                      background: '#E53E3E',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Delete account
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Link to="/dashboard" style={{ color: 'var(--slate)', textDecoration: 'none', fontSize: '0.9rem' }}>
                    ← Back to Dashboard
                  </Link>
                </div>
              </div>
            )}

            <Modal
              isOpen={isDeleteModalOpen}
              title="Delete your account"
              confirmLabel={isDeleting ? 'Deleting...' : 'Delete permanently'}
              cancelLabel="Cancel"
              confirmVariant="danger"
              onCancel={closeDeleteModal}
              onConfirm={handleDeleteAccount}
              closeOnBackdrop={!isDeleting}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                  To confirm deletion, type <strong style={{ color: 'var(--text-primary)' }}>DELETE</strong> below and enter your current password.
                </p>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                    Confirmation
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                    autoComplete="off"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `1px solid ${deleteConfirmText !== '' && deleteConfirmText !== 'DELETE' ? 'var(--error-text)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                {!isOAuth && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                      Current password
                    </label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                )}

                {deleteError && (
                  <div style={{ padding: '0.75rem 1rem', borderRadius: '8px', background: '#FED7D7', color: '#C53030', fontSize: '0.875rem' }}>
                    {deleteError}
                  </div>
                )}
              </div>
            </Modal>
          </div>
        </div>
      </main>

      {/* Bottom snackbar */}
      <div
        style={{
          position: 'fixed',
          bottom: isSnackbarVisible ? '1.5rem' : '-6rem',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'min(90vw, 640px)',
          padding: '0.875rem 1.25rem',
          borderRadius: '12px',
          background: 'var(--forest-green)',
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          transition: 'bottom 0.3s ease',
          zIndex: 2000,
          pointerEvents: 'none',
          textAlign: 'center'
        }}
      >
        {snackbarMessage}
      </div>
    </div>
  )
}
